<script setup>
import LoadingOverlay from '@/components/basic/atoms/LoadingOverlay.vue'
import TheOpcStage from '@/components/opc/molecules/TheOpcStage.vue'
import TheOpcBody from '@/components/opc/organisms/TheOpcBody.vue'
import TheOpcHeader from '@/components/opc/organisms/TheOpcHeader.vue'
import { TYPES } from '@/constants/AdminTool'
import { useCustomerStore } from '@/stores/customer'
import { useFeaturesStore } from '@/stores/features'
import { useNewCheckoutStore } from '@/stores/newCheckout'
import { useOpcStore } from '@/stores/opc'
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

const LazyComponent = defineAsyncComponent(
  () => import('@/components/basic/atoms/LazyComponent.vue')
)
const Tool = defineAsyncComponent(
  () => import('@/components/basic/atoms/Tool.vue')
)
const MultiTools = defineAsyncComponent(
  () => import('@/components/basic/molecules/MultiTools.vue')
)
const TheOpcFooter = defineAsyncComponent(
  () => import('@/components/opc/organisms/TheOpcFooter.vue')
)
const TheOpcSoldOutOverlay = defineAsyncComponent(
  () => import('@/components/opc/organisms/TheOpcSoldOutOverlay.vue')
)
const TheOpcErrorMessage = defineAsyncComponent(
  () => import('@/components/opc/atoms/TheOpcErrorMessage.vue')
)
const TheOpcStageInfo = defineAsyncComponent(
  () => import('@/components/opc/molecules/TheOpcStageInfo.vue')
)

const router = useRouter()
const route = useRoute()

const { smAndUp } = useDisplay()

const adminTool = TYPES
const soldOut = ref(false)

const checkoutStore = useNewCheckoutStore()
// Use computed to safely access checkoutError to avoid storeToRefs issues with null values
const checkoutError = computed(() => checkoutStore.checkoutError)

const opcStore = useOpcStore()
const { product, loading } = storeToRefs(opcStore)

const customerStore = useCustomerStore()
const { customerIsAdmin } = storeToRefs(customerStore)

const featuresStore = useFeaturesStore()
const { isFeatureEnabled } = storeToRefs(featuresStore)

watch(product, () => {
  const error = route.query.error
  if (error) {
    checkoutStore.checkoutError = error
  }
  soldOut.value = !product.value?.stocks?.some(
    ({ is_in_stock: isInStock }) => isInStock
  )
})

watch(soldOut, (isSoldOut) => {
  document.body.style.overflow = isSoldOut ? 'hidden' : null
  document.body.style.height = isSoldOut ? '100vh' : null
})

onMounted(() => {
  window.scrollTo({ top: 0, behavior: 'instant' })
})
</script>

<template>
  <div class="position-relative">
    <LoadingOverlay v-if="loading" />
    <TheOpcHeader />
    <main id="page-template">
      <TheOpcStage data-elblink="product:child" />
      <div
        v-if="!smAndUp"
        class="stage-info v-col-12 v-col-lg-5 mx-auto px-0 align-self-center"
      >
        <TheOpcStageInfo class="px-3 pb-0 pb-sm-2" />
      </div>
      <TheOpcErrorMessage />
      <TheOpcBody data-elblink="product:parent" />
      <LazyComponent id="opc-footer" :delay="1500">
        <TheOpcFooter />
      </LazyComponent>
    </main>
    <MultiTools id="fixed-tools" v-if="customerIsAdmin && product">
      <Tool :type="adminTool.TRANSLATE_MODE" />
      <Tool :type="adminTool.HIGHLIGHT_CMS_ITEMS" />
      <Tool :type="adminTool.TOGGLE_DATALAYER_LOGGING" />
      <Tool :type="adminTool.SHOW_ELBWALKER_USER_DATA" />
      <Tool
        :type="adminTool.EDIT_PRODUCT"
        :id="product.product_id"
        :campaign-id="product.campaign_id"
      />
      <Tool :type="adminTool.SET_CUSTOMER_ID" />
    </MultiTools>
    <TheOpcSoldOutOverlay
      v-if="soldOut"
      :show-hide-button="customerIsAdmin"
      @hide="soldOut = false"
    />
  </div>
</template>
